/**
 * 考試規劃 — 日期→範圍判定(純函式)。
 * Source of truth: docs/spec/EXAM_PLANNER_SPEC.md §1, §3。
 *
 * 這是「AI 針對日期區間整理範圍」的大腦:給一串考試事件,算出每次考試的
 * 複習日期區間 `(from, to]`;材料(帶 date)按日期落入哪個區間決定屬於哪次考試。
 *
 *   段考(期中/期末)= 累積:from = 上一個「段考」日期(跳過小考),無則 null。
 *   小考          = 接續:from = 上一個「任何」考試日期,無則 null。
 *   ⇒ 段考大區間包住其內小考小區間(重疊,正常)。
 *
 * 日期一律 'YYYY-MM-DD' 字串;同格式可直接字典序比較(不需 Date 物件)。
 * 純學習側,不碰任何戰鬥數值(雙軌鐵則)。
 */
import type { ExamEvent, ExamWindow, Subject } from './types';
import { isSegmentExam } from './types';

/** 兩個日期取較晚者(null 視為負無限)。 */
function laterDate(a: string | null, b: string | null): string | null {
  if (a === null) return b;
  if (b === null) return a;
  return a >= b ? a : b;
}

/** 依日期升冪排序(穩定:同日保留輸入順序)。 */
function sortedByDate(events: ExamEvent[]): ExamEvent[] {
  return events
    .map((e, i) => ({ e, i }))
    .sort((a, b) => (a.e.date < b.e.date ? -1 : a.e.date > b.e.date ? 1 : a.i - b.i))
    .map((x) => x.e);
}

/**
 * 算每次考試的複習區間 `(from, to]`。回傳依日期升冪。
 *   段考(全科)from = 前一個段考日期。
 *   小考(有科目)from = 前一個「同科小考」或「段考」中較晚者(段考全科會重置各科基準)。
 *   小考(無科目)from = 前一個任何考試日期。
 *   皆無則 null(= 開學前全部)。
 */
export function computeWindows(events: ExamEvent[]): ExamWindow[] {
  const sorted = sortedByDate(events);
  const windows: ExamWindow[] = [];
  let lastAnyDate: string | null = null; // 最近一次「任何」考試日期
  let lastSegDate: string | null = null; // 最近一次「段考」日期
  const lastQuizBySubject = new Map<Subject, string>(); // 各科最近一次小考日期

  for (const event of sorted) {
    let from: string | null;
    if (isSegmentExam(event.type)) {
      from = lastSegDate;
    } else if (event.subject) {
      from = laterDate(lastSegDate, lastQuizBySubject.get(event.subject) ?? null);
    } else {
      from = lastAnyDate;
    }
    windows.push({ event, from, to: event.date });

    lastAnyDate = event.date;
    if (isSegmentExam(event.type)) lastSegDate = event.date;
    else if (event.subject) lastQuizBySubject.set(event.subject, event.date);
  }
  return windows;
}

/** 某日期是否落在區間 `(from, to]`(from=null 視為負無限)。 */
export function windowContains(w: ExamWindow, date: string): boolean {
  return (w.from === null || w.from < date) && date <= w.to;
}

/** 帶 date 欄位的材料(考卷/作業/單字/錯題的共同形狀)。 */
export interface Dated {
  date: string;
}

/** 過濾出 date 落在某考試範圍內的材料。 */
export function materialsInScope<T extends Dated>(w: ExamWindow, materials: T[]): T[] {
  return materials.filter((m) => windowContains(w, m.date));
}

/**
 * 一張新上傳的材料(帶日期)該歸到哪一次考試:歸到「`to >= date` 的最早一次」
 * = 那是「這份材料之後最近要考的那次」(它正在準備的那場)。全部都比它早 → null。
 */
export function examForUpload(events: ExamEvent[], date: string): ExamEvent | null {
  const sorted = sortedByDate(events);
  for (const e of sorted) if (e.date >= date) return e;
  return null;
}
